import React, { Component, Ref } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import Calendar from 'react-calendar';
import { Calendar as BigCalendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarPage.css';
import { withTranslation, WithTranslation } from "react-i18next";

// Import SVG icons - Following GoalMainPage pattern
import { ReactComponent as MyBookingsIcon } from '../../../assets/icons/my-bookings.svg';
import { ReactComponent as AddIcon } from '../../../assets/icons/add.svg';

// Import Redux actions and selectors (Phase 3 - Redux Integration)
import GetCalendarEventsActions from '../../../redux/GetCalendarEventsRedux';
import { GetCalendarEventsSelectors } from '../../../redux/GetCalendarEventsRedux';
import i18n from '../../../i18n';
import {
  buildAgendaForDateRange,
  buildAgendaFromCalendar,
  ensureDateRangeKeys,
} from './calendarAgendaUtils';

// Localizer for react-big-calendar
const localizer = momentLocalizer(moment);

const setMomentLocale = (lang: string) => {
  if (lang === 'zh') {
    // @ts-ignore
    require('moment/locale/zh-cn');
    moment.locale('zh-cn');
  } else {
    moment.locale('en');
  }
};


interface CalendarPageProps extends WithTranslation {
  navigate: NavigateFunction;
  // Phase 3 - Redux props
  getCalendarEventsFetching: boolean;
  getCalendarEventsSuccess: boolean;
  getCalendarEventsError: boolean;
  calendar: any;
  getCalendarEvents: (navigate: any) => void;
}

interface CalendarPageState {
  loading: boolean;
  currentDate: Date;
  isCalendarOpened: boolean;
  // Phase 4 - Calendar functionality state
  agenda: any;
  calendar: any;
  isReady: boolean;
  // Phase 5 - Event management state for react-big-calendar
  events: any[];
  selectedEvent: any;
  showEventModal: boolean;
  currentView: string;
  showQuickActions: boolean;
  showAgendaView: boolean; // Toggle between calendar and agenda views
}

class CalendarPage extends Component<CalendarPageProps, CalendarPageState> {
  // Event type definitions - like reference project
  publicHoliday = { key: 'booking', color: 'red' };
  centerHoliday = { key: 'booking', color: 'blue' };
  event = { key: 'event', color: 'yellow' };
  staff = { key: 'event', color: 'orange' };
  booking = { key: 'booking', color: 'black' };
  agendaContentRef: Ref<HTMLDivElement> | null | undefined;

  constructor(props: CalendarPageProps) {
    super(props);
    this.state = {
      loading: false,
      currentDate: new Date(),
      isCalendarOpened: false,
      agenda: {},
      calendar: {},
      isReady: false,
      // react-big-calendar state
      events: [],
      selectedEvent: null,
      showEventModal: false,
      currentView: 'month',
      showQuickActions: false,
      showAgendaView: false // Start with calendar view
    };
  }

  // Phase 4 - Calendar data processing like reference project
  static getDerivedStateFromProps(nextProps: any, prevState: any) {
    // console.log('getDerivedStateFromProps ---- ', nextProps);
    if (prevState.props) {
      if (!nextProps.getCalendarEventsFetching) {
        if (nextProps.getCalendarEventsSuccess && nextProps.getCalendarEventsSuccess !== prevState.props.getCalendarEventsSuccess) {
          // Keep all API months in agenda so month navigation can show events
          const { calendar, agenda } = buildAgendaFromCalendar(nextProps.calendar);

          const currentMonthStart = moment().startOf('month');
          const currentMonthEnd = moment().endOf('month');
          const startDate = currentMonthStart.clone().subtract(7, 'days');
          const endDate = currentMonthEnd.clone().add(7, 'days');
          ensureDateRangeKeys(calendar, agenda, startDate, endDate);

          return {
            calendar,
            agenda,
            isReady: true,
            props: nextProps
          };
        }
      }
    }

    return {
      props: nextProps
    };
  }

  componentDidMount() {
    console.log('CalendarPage mounted with navigate prop:', this.props.navigate);
    
    // Load calendar events from Redux
    this.props.getCalendarEvents(this.props.navigate);
    
    // Initialize calendar data
    this.initializeCalendarData();

    console.log('CalendarPage mounted with i18n language:', i18n.language);

    setMomentLocale(i18n.language);
    // moment.locale(i18n.language == 'zh' ? 'zh-cn' : 'en');
    
  }

  _onMyBookingPress = () => {
    this.props.navigate('/calendar/my-booking');
  };

  _onAddBookingPress = () => {
    this.props.navigate('/calendar/select-booking-category');
  };

  handleDateChange = (value: any) => {
    if (value instanceof Date) {
      this.setState({ currentDate: value });
      console.log('Date selected:', value);
      
      // Load items for the new month if needed
      this.loadItemsForMonth(moment(value));
    }
  };

  // Phase 2 - Month bar toggle like reference project (Enhanced)
  onCalendarToggled = (calendarOpened: boolean) => {
    this.setState({ isCalendarOpened: calendarOpened });
  };

  // Handle month navigation and update agenda view
  handleMonthChange = (newDate: Date) => {
    // Set current date to first day of the new month
    const firstDayOfMonth = moment(newDate).startOf('month').toDate();
    
    console.log('Month changing to:', moment(newDate).format('YYYY-MM'), 'Setting current date to:', moment(firstDayOfMonth).format('YYYY-MM-DD'));
    
    // Update state with new current date first
    this.setState({ 
      currentDate: firstDayOfMonth 
    }, () => {
      // After state update, load items for the new month
      this.loadItemsForMonth(moment(newDate));
      
      // Refresh calendar data for the new month
      // this.props.getCalendarEvents(this.props.navigate);
      
      // Scroll to the first day of the month in agenda view
      setTimeout(() => {
        const firstDayElement = document.getElementById(`ref-${moment(firstDayOfMonth).format('YYYY-MM-DD')}`);
        if (firstDayElement && this.agendaContentRef) {
          firstDayElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    });
  };

  // Phase 4 - Calendar functionality methods
  initializeCalendarData = () => {
    // Initialize calendar with current month data instead of 2-year data
    const calendar: { [key: string]: any } = {};
    const agenda: { [key: string]: any[] } = {};
    
    const currentDate = new Date();
    const currentMonthStart = moment(currentDate).startOf('month');
    const currentMonthEnd = moment(currentDate).endOf('month');
    
    // Add buffer days for smooth navigation (previous and next month)
    const startDate = currentMonthStart.clone().subtract(7, 'days');
    const endDate = currentMonthEnd.clone().add(7, 'days');
    
    for (let date = startDate.clone(); date.isSameOrBefore(endDate); date.add(1, 'day')) {
      const strTime = date.format('YYYY-MM-DD');
      calendar[strTime] = {};
      agenda[strTime] = [];
    }
    
    this.setState({ calendar, agenda, isReady: true });
  };

  // Phase 5 - Complete Calendar Logic (Missing Methods from Reference Project)
  
  // Load items for specific month - pull from full Redux calendar (all months from API)
  loadItemsForMonth = (date: any) => {
    const monthStart = moment(date).startOf('month');
    const monthEnd = moment(date).endOf('month');
    const startDate = monthStart.clone().subtract(7, 'days');
    const endDate = monthEnd.clone().add(7, 'days');

    const newAgenda = buildAgendaForDateRange(this.props.calendar, startDate, endDate);
    console.log('newAgenda', newAgenda);

    this.setState(prevState => ({
      agenda: {
        ...prevState.agenda,
        ...newAgenda
      },
      isReady: true
    }));
  };

  // Phase 5 - Event management methods for react-big-calendar
  handleViewChange = (newView: string) => {
    this.setState({ currentView: newView });
  };

  handleEventSelect = (event: any) => {
    this.setState({ selectedEvent: event, showEventModal: true });
  };

  eventStyleGetter = (event: any) => {
    return {
      style: {
        backgroundColor: event.backgroundColor,
        borderColor: event.borderColor,
        color: '#FFFFFF',
        borderRadius: '4px',
        opacity: 0.8,
        border: '0px',
        display: 'block'
      }
    };
  };

  // Agenda View Methods - Like Reference Project
  renderAgendaItems = () => { 
    // Phase 4 - Use local state agenda data like reference project
    const currentDateString = moment(this.state.currentDate).format('YYYY-MM-DD');
    const currentMonth = moment(this.state.currentDate).format('YYYY-MM');
    
    console.log('Rendering agenda items for month:', currentMonth, 'Current date:', currentDateString);
    
    // Filter dates to show only current month data instead of 2-year data
    const dateList = Object.keys(this.state.agenda)
      .filter(date => {
        const dateMonth = moment(date).format('YYYY-MM');
        return dateMonth === currentMonth;
      })
      .sort((a, b) => moment(a).valueOf() - moment(b).valueOf()); // Sort dates chronologically
    
    console.log('Filtered date list for agenda:', dateList);

    // If no dates found for current month, show a message
    if (dateList.length === 0) {
      return (
        <div className="agenda-empty-state">
          <p>No events found for {moment(this.state.currentDate).format('MMMM YYYY')}</p>
        </div>
      );
    }

    return dateList.map((date: any, index: number) => {
      const displayDate = i18n.language == 'zh' ? moment(date).format('Do') : moment(date).format('DD');
      const dayName = moment(date).format('ddd');
      const firstDayOfMonth = moment(date).format('YYYY-MM-DD');

      const dayEvents = this.state.agenda[date] || [];
      
      // Check if this date is the current selected date (exclude today from active)
      const isCurrentDate = date === currentDateString;
      // const isCurrentDate = date === currentDateString && date !== moment().format('YYYY-MM-DD');
      const isToday = date === moment().format('YYYY-MM-DD');

      return (            
        <div key={index} className={`agenda-item ${isCurrentDate ? 'active' : ''} ${isToday ? 'today' : ''}`}>
          {
            ( firstDayOfMonth === currentDateString ) ?
            <input type="text" id={`ref-${date}`} value={date} hidden readOnly />
            :
            null
          }
          <div className="agenda-item-date">
            <strong>{displayDate}</strong>
            <p>{dayName}</p>
          </div>
          <div className="agenda-item-event-main">
            {
              dayEvents.map((event: any, index2: number) => {
                return (
                  <div key={index2} className="agenda-item-event">
                    <div className="agenda-item-left">
                      <span className="event-dot event-dot--event"></span>
                      <span className="agenda-item-name">{event.name || 'Event'}</span>
                    </div>                      
                  </div>
                )
              })
            }
          </div>
        </div>
      )
    });
  };

  // Phase 4 - Calendar tile rendering methods
  renderTileContent = ({ date, view }: any) => {
    if (view === 'month') {
      const dateString = moment(date).format('YYYY-MM-DD');
      const dayEvents = this.state.agenda[dateString] || [];
      
      if (dayEvents.length > 0) {
        return (
          <div className="calendar-event-markers">
            {dayEvents.slice(0, 3).map((event: any, index: number) => (
              <div 
                key={index} 
                className={`event-marker event-marker--${event.type || 'event'}`}
                title={event.name}
              />
            ))}
          </div>
        );
      }
    }
    return null;
  };

  getTileClassName = ({ date, view }: any) => {
    if (view === 'month') {
      const dateString = moment(date).format('YYYY-MM-DD');
      const dayEvents = this.state.agenda[dateString] || [];
      
      if (dayEvents.length > 0) {
        return 'calendar-tile--has-events';
      }
    }
    return '';
  };

  

  render() {
    const { loading, currentDate, showAgendaView, events, currentView, showEventModal } = this.state;
    const { getCalendarEventsFetching, calendar } = this.props;
    const { t } = this.props;

    return (
      <div className="calendar-page">

          {/* Header Section - Following GoalMainPage design pattern */}
          <header className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button 
                  className="header-button calendar-action-button my-booking-button"
                  onClick={this._onMyBookingPress}
                  type="button"
                >
                  <MyBookingsIcon />
                </button>
                <h1 className="header-title">
                  { t('tab:title.calendar') }
                </h1>                
              </div>                
              <div className="header-right">
                <button 
                  className="header-button add-booking-button"
                  onClick={this._onAddBookingPress}
                  type="button"
                >
                  <AddIcon />
                </button>
              </div>
            </div>
          </header>
        <div className="calendar-container">       

          {/* Calendar Action Header - Following reference CalendarScreen design */}
          <div className="calendar-action-body">
            <div hidden className={`${this.state.isCalendarOpened ? 'month-display-hide' : 'month-display-show'}`}>
              <span className="month-text">
                {moment(currentDate).format('MMM YYYY')}
              </span>
            </div>
            
            {/* View Toggle Button */}
            <div className="view-toggle" style={{display: 'none'}}>
              <button 
                className={`toggle-btn ${!showAgendaView ? 'active' : ''}`}
                onClick={() => this.setState({ showAgendaView: false })}
                type="button"
              >
                Calendar
              </button>
              <button 
                className={`toggle-btn ${showAgendaView ? 'active' : ''}`}
                onClick={() => this.setState({ showAgendaView: true })}
                type="button"
              >
                Agenda
              </button>
            </div>
          </div>

          {this.props.getCalendarEventsFetching && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          )}       

          {/* Main Content */}
          {/*
          !this.props.getCalendarEventsFetching
          */}
          {(
          <div className="calendar-body">
            
            {/* Calendar Component - Phase 5 Enhanced ✅ */}
            {!showAgendaView && (
              <div className="calendar-component">
                <Calendar
                  locale={i18n.language}
                  onChange={this.handleDateChange}
                  value={currentDate}
                  className="react-calendar"
                  maxDate={new Date(new Date().getFullYear() + 1, 11, 31)}
                  minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
                  onActiveStartDateChange={(value) => {
                    console.log('Active start date changed to:', value);
                    if ( typeof value == 'object') {
                      if ( (value.view == 'month' && value.action == 'prev') || (value.view == 'month' && value.action == 'next') ){
                        this.handleMonthChange(value.activeStartDate || new Date());
                      }
                    }

                    this.onCalendarToggled(true);
                    if (value instanceof Date) {
                      this.handleMonthChange(value);
                    }
                  }}
                  onViewChange={({ view, activeStartDate }) => {
                    console.log('View changed to:', view, 'Active start date:', activeStartDate);
                    // Fires when switching between month/year/decade views
                    // Optional: track view changes
                  }}
                  // Phase 4 - Add event markers
                  tileContent={this.renderTileContent}
                  tileClassName={this.getTileClassName}

                  prev2Label={null}
                  next2Label={null}
                />
              </div>
            )}

            {/* Agenda View with react-big-calendar 
            {showAgendaView && (
              <div className="agenda-calendar-component" style={{display: 'none'}}>
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 500 }}
                  view={currentView as any}
                  onView={this.handleViewChange}
                  onSelectEvent={this.handleEventSelect}
                  eventPropGetter={this.eventStyleGetter}
                  defaultView="month"
                  views={['month', 'week', 'day', 'agenda']}
                  step={60}
                  timeslots={1}
                  selectable
                  popup
                  onNavigate={(newDate: Date) => this.setState({ currentDate: newDate })}
                  date={currentDate}
                />
              </div>
            )}
            */}

            <div className="agenda-container">
              <div 
                className="agenda-content" 
                ref={this.agendaContentRef}
                key={`agenda-${moment(currentDate).format('YYYY-MM')}`}
              >
                {this.renderAgendaItems()}
              </div>
            </div>

          </div>
          )}
        </div>
      </div>
    );
  }
}

// Redux mapping - like reference project
const mapStateToProps = (state: any) => {
  return {
    getCalendarEventsFetching: GetCalendarEventsSelectors.isFetching(state),
    getCalendarEventsSuccess: GetCalendarEventsSelectors.isSuccess(state),
    getCalendarEventsError: GetCalendarEventsSelectors.hasError(state),
    calendar: GetCalendarEventsSelectors.getData(state)
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    getCalendarEvents: (navigate: any) => dispatch(GetCalendarEventsActions.getCalendarEventsRequest(navigate))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(CalendarPage));
